import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { MessageCircle, MessageSquare } from "lucide-react";

const EyewearOrders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [expandedRowId, setExpandedRowId] = useState(null);

  const baseUrl = process.env.REACT_APP_CHE_SERVICES_URL;
  const cartBaseUrl = process.env.REACT_APP_CART_SERVICES_URL;

  const fetchData = async (pageNumber = 0, searchValue = "") => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${baseUrl}eyeWearOrder/list`, {
        params: {
          pageNumber,
          pageSize,
          search: searchValue || undefined,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (response.data && response.data.content) {
        const serverPageNumber =
            typeof response.data.pageNumber === "number"
                ? response.data.pageNumber
                : pageNumber;
        const serverPageSize =
            typeof response.data.pageSize === "number"
                ? response.data.pageSize
                : pageSize;
        const total = response.data.totalElements || 0;

        setOrders(response.data.content);
        setTotalElements(total);
        // If backend doesn't send totalPages, derive it from totalElements & pageSize
        const derivedTotalPages =
            response.data.totalPages ||
            (serverPageSize > 0 ? Math.ceil(total / serverPageSize) : 0);
        setTotalPages(derivedTotalPages);
        setCurrentPage(serverPageNumber);
      }
    } catch (error) {
      console.error("Error fetching eyewear orders:", error);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(0, search);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      fetchData(newPage, search);
    }
  };

  const handleSearch = () => {
    // Always start from first page when applying a new search
    setCurrentPage(0);
    fetchData(0, search.trim());
  };

  const viewPrescription = async (orderData) => {
    const docName = orderData.order?.prescriptionUrl;

    if (!docName) {
      toast.error("No prescription available for this order");
      return;
    }

    try {
      const response = await axios.get(
          `${cartBaseUrl}eyewear/cart/view-prescription`,
          {
            params: { docName },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
      );

      const signedUrl = response.data?.signedUrl;

      if (signedUrl) {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Failed to load prescription");
      }
    } catch (error) {
      console.error("Error fetching prescription URL:", error);
      toast.error(
          error.response?.data?.message || "Failed to load prescription",
      );
    }
  };

  const handleOrderFulfilled = async (orderId) => {
    try {
      const response = await axios.put(
          `${baseUrl}eyeWearOrder/update-status?orderId=${orderId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
      );

      if (response.data) {
        toast.success("Order marked as fulfilled successfully");
        // Refresh the data
        fetchData(currentPage, search);
      }
    } catch (error) {
      console.error("Error fulfilling order:", error);
      toast.error(
          error.response?.data?.message || "Failed to mark order as fulfilled",
      );
    }
  };

  return (
      <div className="p-3 sm:pr-0 sm:pb-0 sm:pl-10 sm:pt-10 w-full sm:w-[99%] bg-white rounded-2xl shadow-md">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#004276]">Eyewear Orders</h1>
        </div>

        {/* Search filter */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              placeholder="Product Id/Mobile/Patient Code"
              className="border border-gray-300 rounded px-3 py-2 text-sm flex-1 min-w-0 sm:flex-none w-64 focus:outline-none focus:ring-2 focus:ring-[#004276]"
          />
          <button
              onClick={handleSearch}
              className="px-4 py-2 bg-[#004276] text-white rounded text-sm hover:bg-[#00325a] transition-colors"
          >
            Search
          </button>
        </div>

        {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">Loading...</div>
            </div>
        ) : (
            <>
              <div className="overflow-x-auto border border-[#004276] rounded-md">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-[#004276] text-white">
                  <tr>
                    <th className="p-4 font-semibold min-w-[200px]">Patient Details</th>
                    <th className="p-4 font-semibold">Order ID</th>
                    <th className="p-4 font-semibold">Transaction ID</th>
                    <th className="p-4 font-semibold min-w-[130px]">Order Date</th>
                    <th className="p-4 font-semibold min-w-[250px]">Product Details</th>
                    <th className="p-4 font-semibold min-w-[140px]">Price</th>
                    <th className="p-4 font-semibold">Order Status</th>
                    <th className="p-4 font-semibold">
                      Order Fulfillment Status
                    </th>
                    <th className="p-4 font-semibold min-w-[220px]">
                      Eclinic Details
                    </th>
                    <th className="p-4 font-semibold min-w-[220px]">
                      Retailer Details
                    </th>
                    <th className="p-4 font-semibold min-w-[220px]">
                      Distributor Address
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  {orders.length > 0 ? (
                      orders.map((item, index) => (
                          <React.Fragment key={item.order?.orderId || index}>
                            <tr
                                onClick={() =>
                                    setExpandedRowId(
                                        expandedRowId === item.order?.orderId
                                            ? null
                                            : item.order?.orderId,
                                    )
                                }
                                className={`border-t cursor-pointer hover:bg-gray-50 transition-colors ${index % 2 === 0 ? "bg-[#F6F6F6]" : "bg-white"}`}
                            >
                              <td className="p-4">
                                <div className="flex flex-col gap-1.5">
                                  <div className="font-medium text-[15px]">
                                    {item.order?.patientName || "-"}
                                  </div>
                                  <div className="text-[13px] text-gray-600">
                                    Patient Code: {item.order?.patientId || "-"}
                                  </div>
                                  <div className="text-[13px] text-gray-600">
                                    Mobile: {item.order?.patientMobile || "-"}
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">{item.order?.orderId || "-"}</td>
                              <td className="p-4">{item.order?.transactionId || "-"}</td>
                              <td className="p-4">
                                {item.order?.createdAt
                                    ? new Date(item.order.createdAt).toLocaleDateString(
                                        "en-GB",
                                        {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        }
                                    )
                                    : "-"}
                              </td>
                              <td className="p-4 text-[13.5px] leading-relaxed">
                                {item.items && item.items.length > 0
                                    ? item.items.map((product, idx) => (
                                        <div key={idx} className="mb-3 last:mb-0">
                                          <div className="font-semibold text-gray-800">
                                            {product.productName || "-"}
                                          </div>
                                          <div className="text-[12.5px] text-gray-600 mt-1">
                                            Sku code: {product.productCode || "-"}
                                          </div>
                                          {product.frameType && (
                                              <div className="text-[12.5px] text-gray-600">
                                                Frame type: {product.frameType}
                                              </div>
                                          )}
                                          {idx < item.items.length - 1 && (
                                              <hr className="my-3 border-gray-100" />
                                          )}
                                        </div>
                                    ))
                                    : "-"}
                              </td>
                              <td className="p-4">
                                <div className="flex flex-col gap-1.5 text-[13px]">
                                  <div className="text-gray-600">Total: ₹{item.order?.totalAmount ?? "-"}</div>
                                  <div className="text-gray-600">
                                    Discount: ₹{item.order?.curecoinDiscount ?? 0}
                                  </div>
                                  <div className="font-bold text-[#004276] text-[14px]">
                                    Final: ₹{item.order?.finalAmount ?? "-"}
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                        <span
                            className={`px-3 py-1.5 rounded text-[12.5px] font-semibold ${
                                item.order?.orderStatus === "ORDER_FULFILLED" ||
                                item.order?.orderStatus === "FULFILLED"
                                    ? "bg-green-100 text-green-800 border border-green-200"
                                    : item.order?.orderStatus === "ORDER_PLACED"
                                        ? "bg-blue-100 text-blue-800 border border-blue-200"
                                        : item.order?.orderStatus === "CANCELLED"
                                            ? "bg-red-100 text-red-800 border border-red-200"
                                            : "bg-gray-100 text-gray-800 border border-gray-200"
                            }`}
                        >
                          {item.order?.orderStatus || "-"}
                        </span>
                              </td>
                              <td className="p-4">
                        <span
                            className={`px-3 py-1.5 rounded text-[12.5px] font-semibold ${
                                item.order?.orderFulfillment === "COMPLETED" ||
                                item.order?.orderFulfillmentStatus ===
                                "FULFILLED" ||
                                item.order?.fulfillmentStatus === "FULFILLED" ||
                                item.order?.orderStatus === "ORDER_FULFILLED" ||
                                item.order?.orderStatus === "FULFILLED"
                                    ? "bg-green-100 text-green-800 border border-green-200"
                                    : item.order?.orderFulfillmentStatus ===
                                    "PENDING" ||
                                    item.order?.fulfillmentStatus === "PENDING" ||
                                    item.order?.orderFulfillment === "PENDING"
                                        ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                                        : "bg-gray-100 text-gray-800 border border-gray-200"
                            }`}
                        >
                          {item.order?.orderFulfillment ||
                              item.order?.orderFulfillmentStatus ||
                              item.order?.fulfillmentStatus ||
                              (item.order?.orderStatus === "ORDER_FULFILLED" ||
                              item.order?.orderStatus === "FULFILLED"
                                  ? "FULFILLED"
                                  : "PENDING") ||
                              "-"}
                        </span>
                              </td>
                              <td className="p-4 align-top">
                                <div className="text-[13.5px] leading-relaxed">
                                  {item.order?.sourceChannel === "ECLINIC" ? (
                                      <div className="flex flex-col gap-1.5">
                                        <div>
                                          <span className="font-semibold text-[#6B7280]">Eclinic Name: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.clinicAddress[0]?.hospitalName || "-"}
                                </span>
                                        </div>
                                        <div>
                                          <span className="font-semibold text-[#6B7280]">Eclinic Address: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.clinicAddress[0]?.address1 || "-"}
                                            {item.clinicAddress[0]?.address2 &&
                                                `, ${item.clinicAddress[0].address2}`}
                                            {item.clinicAddress[0]?.city &&
                                                `, ${item.clinicAddress[0].city}`}
                                            {item.clinicAddress[0]?.state &&
                                                `, ${item.clinicAddress[0].state}`}
                                            {item.clinicAddress[0]?.pincode &&
                                                ` ${item.clinicAddress[0].pincode}`}
                                </span>
                                        </div>
                                        <div>
                                          <span className="font-semibold text-[#6B7280]">Eclinic Staff Name: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.userData[0]
                                      ? `${item.userData[0].firstName} ${item.userData[0].lastName}`
                                      : "-"}
                                </span>
                                        </div>
                                        <div>
                                          <span className="font-semibold text-[#6B7280]">Mobile No: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.userData[0]?.mobile || "-"}
                                </span>
                                        </div>
                                      </div>
                                  ) : (
                                      <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="p-4 align-top">
                                <div className="text-[13.5px] leading-relaxed">
                                  {item.order?.paymentMode === "B2B Credit" || item.order?.sourceChannel === "Swasthya Mitra" ? (
                                      <div className="flex flex-col gap-1.5">
                                        <div>
                                          <span className="font-semibold text-[#6B7280]">Retailer Name: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.userData[0]
                                      ? `${item.userData[0].firstName} ${item.userData[0].lastName}`
                                      : "-"}
                                </span>
                                        </div>
                                        <div>
                                          <span className="font-semibold text-[#6B7280]">Mobile No: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.userData[0]?.mobile || "-"}
                                </span>
                                        </div>
                                        <div>
                                          <span className="font-semibold text-[#6B7280]">Retailer Address: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.clinicAddress[0]?.address1 || "-"}
                                            {item.clinicAddress[0]?.address2 &&
                                                `, ${item.clinicAddress[0].address2}`}
                                            {item.clinicAddress[0]?.city &&
                                                `, ${item.clinicAddress[0].city}`}
                                            {item.clinicAddress[0]?.state &&
                                                `, ${item.clinicAddress[0].state}`}
                                            {item.clinicAddress[0]?.pincode &&
                                                ` ${item.clinicAddress[0].pincode}`}
                                </span>
                                        </div>
                                        {/* <div>
                                <span className="font-semibold text-[#6B7280]">Retailer Staff Name: </span>
                                <span className="text-gray-900 font-medium">
                                  {item.userData[0]
                                    ? `${item.userData[0].firstName} ${item.userData[0].lastName}`
                                    : "-"}
                                </span>
                              </div> */}

                                      </div>
                                  ) : (
                                      <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="p-4 align-top">
                                <div className="text-[13.5px] leading-relaxed">
                                  {item.order?.sourceChannel === "Swasthya Mitra" ? (
                                      <>
                                        <span className="font-semibold text-[#6B7280]">Distributor Address: </span>
                                        <span className="text-gray-900 font-medium">Samparna Agency, C/O-Jaga Video & CD, At. Uttreswaer, Near Lingraj ice factory, Soro, Dist - Balasore</span>
                                      </>
                                  ) : (
                                      <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {expandedRowId === item.order?.orderId && (
                                <tr className="bg-blue-50/50 border-b">
                                  <td colSpan="11" className="p-4">
                                    <div className="flex flex-col gap-4">
                                      <div className="flex flex-wrap items-center gap-x-6 sm:gap-x-12 gap-y-2 text-[13.5px]">
                                        <div>
                                          <span className="font-semibold text-gray-500">Ship To: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.order?.sourceChannel === "ECLINIC" ? "Eclinic" : "Distributor"}
                                </span>
                                        </div>
                                        <div>
                                          <span className="font-semibold text-gray-500">Shipping Address: </span>
                                          <span className="text-gray-900 font-medium">
                                  {item.order?.sourceChannel === "ECLINIC" ? (
                                      <>
                                        {item.clinicAddress[0]?.address1 || "-"}
                                        {item.clinicAddress[0]?.address2 &&
                                            `, ${item.clinicAddress[0].address2}`}
                                        {item.clinicAddress[0]?.city &&
                                            `, ${item.clinicAddress[0].city}`}
                                        {item.clinicAddress[0]?.state &&
                                            `, ${item.clinicAddress[0].state}`}
                                        {item.clinicAddress[0]?.pincode &&
                                            ` ${item.clinicAddress[0].pincode}`}
                                      </>
                                  ) : (
                                      "Samparna Agency, C/O-Jaga Video & CD, At. Uttreswaer, Near Lingraj ice factory, Soro, Dist - Balasore"
                                  )}
                                </span>
                                        </div>
                                      </div>
                                      <div className="flex flex-wrap justify-end items-center gap-4">
                                        <div className="flex flex-wrap gap-3">
                                          {item.order?.prescriptionUrl ? (
                                              <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    viewPrescription(item);
                                                  }}
                                                  className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 transition-colors"
                                              >
                                                View Prescription
                                              </button>
                                          ) : (
                                              <span className="text-gray-400 text-xs">-</span>
                                          )}

                                          {item.order?.orderFulfillment === "COMPLETED" ? (
                                              <button
                                                  disabled
                                                  className="bg-gray-300 text-gray-500 px-3 py-1 rounded text-xs cursor-not-allowed"
                                              >
                                                Order Fulfilled
                                              </button>
                                          ) : item.order?.orderStatus !==
                                          "ORDER_FULFILLED" &&
                                          item.order?.orderStatus !== "FULFILLED" &&
                                          item.order?.orderFulfillmentStatus !==
                                          "FULFILLED" &&
                                          item.order?.fulfillmentStatus !==
                                          "FULFILLED" ? (
                                              <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOrderFulfilled(item.order?.orderId);
                                                  }}
                                                  className="bg-[#00BD7F] text-white px-3 py-1 rounded text-xs hover:bg-[#00a870] transition-colors"
                                              >
                                                Order Fulfilled
                                              </button>
                                          ) : (
                                              <span className="text-green-600 text-xs font-medium">
                                  Fulfilled
                                </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                            )}
                          </React.Fragment>
                      ))
                  ) : (
                      <tr>
                        <td colSpan="11" className="p-4 text-center text-gray-500">
                          No orders found
                        </td>
                      </tr>
                  )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                  <div className="flex flex-wrap justify-center items-center mt-4 mb-8 gap-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                        className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Previous
                    </button>

                    <span className="px-4 py-2 text-sm text-gray-600">
                Page {currentPage + 1} of {totalPages}
              </span>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1}
                        className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Next
                    </button>
                  </div>
              )}
            </>
        )}
      </div>
  );
};

export default EyewearOrders;
